import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { useTranslation } from 'react-i18next'

import { Box, Flex, TertiaryButton, Text } from '@enechain/ecloud-designsystem'
import {
  FaArrowDownToBracketRegular,
  FaCircleQuestionRegular,
} from '@enechain/ecloud-icons'

import { CompactAlert } from '~/components/common/CompactAlert'
import { RegionLoading } from '~/components/common/RegionLoading'
import { WorkflowPageLayout } from '~/components/common/WorkflowPageLayout'
import { iconSize } from '~/constants/iconSize'
import { useCurrentWorkflowCase } from '~/hooks/workflowCase/useCurrentWorkflowCase'
import { downloadCsv } from '~/libs/downloadCsv'
import { AREA_CODE_OPTIONS, convertAreaLabel } from '~/models/area'
import { startOfTodayJst } from '~/models/dateTime'
import { useGetPersonalSettingsQuery } from '~/pages/Settings/hooks/useGetPersonalSettingsQuery'
import { CsvDownloadModal } from '~/pages/SupplyGap/components/CsvDownloadModal'
import { FilterBar } from '~/pages/SupplyGap/components/FilterBar'
import { GapChart } from '~/pages/SupplyGap/components/GapChart'
import { LegendSwatch } from '~/pages/SupplyGap/components/LegendSwatch'
import { SubPeriodSummaryTable } from '~/pages/SupplyGap/components/SubPeriodSummaryTable'
import {
  type UseFilterStateReturn,
  useFilterState,
} from '~/pages/SupplyGap/hooks/useFilterState'
import {
  resolveFetchErrorState,
  useGetSupplyDemandGap,
} from '~/pages/SupplyGap/hooks/useGetSupplyDemandGap'
import {
  buildSupplySeries,
  collectSupplyCategories,
} from '~/pages/SupplyGap/models/supplyCategories'
import {
  CATEGORY_COLOR_MAP,
  type GapFilterState,
  buildCsvRows,
  buildSummaryRows,
  convertAreaSeries,
  resolveQueryAreaCodes,
  toYmd,
} from '~/pages/SupplyGap/models/types'

/**
 * design (`.sg-guide-wrap` / `.sg-guide-panel`) 準拠のガイドボタン + popover。
 * SubPeriodSummaryTable.tsx 側にも同種の実装が要るが、対象ファイルを本ファイルと
 * 同コンポーネントに閉じるため意図的に重複させている (= 新規共有 component は作らない)。
 */
const GuidePopover = ({
  panelId,
  buttonLabel,
  ariaLabel,
  title,
  children,
}: {
  panelId: string
  buttonLabel: string
  ariaLabel: string
  title: string
  children: React.ReactNode
}): React.ReactElement => {
  const [open, setOpen] = useState(false)
  const wrapRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!open) return undefined

    const handlePointerDown = (e: MouseEvent): void => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) {
        setOpen(false)
      }
    }
    const handleKeyDown = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') setOpen(false)
    }
    document.addEventListener('mousedown', handlePointerDown)
    document.addEventListener('keydown', handleKeyDown)
    return () => {
      document.removeEventListener('mousedown', handlePointerDown)
      document.removeEventListener('keydown', handleKeyDown)
    }
  }, [open])

  return (
    <Box ref={wrapRef} position="relative" display="inline-flex">
      <TertiaryButton
        aria-controls={panelId}
        aria-expanded={open}
        aria-label={ariaLabel}
        // ガイドの ? は 14px (UI 辞書 guide-popover.md「`.btn-guide` の icon だけは 14px」)
        leftIcon={
          <FaCircleQuestionRegular
            boxSize={iconSize.guideButton}
            aria-hidden="true"
          />
        }
        onClick={() => setOpen((v) => !v)}
        size="sm"
      >
        {buttonLabel}
      </TertiaryButton>
      {open && (
        <Box
          position="absolute"
          zIndex={50}
          top="calc(100% + 6px)"
          left={0}
          width="380px"
          maxWidth="90vw"
          padding={4}
          background="Surface.primary"
          borderWidth="1px"
          borderColor="Border.lowEmphasis"
          borderRadius="lg"
          boxShadow="lg"
          id={panelId}
          role="dialog"
        >
          <Text marginBottom={2} fontWeight="semibold">
            {title}
          </Text>
          {children}
        </Box>
      )}
    </Box>
  )
}

export const SupplyGapPage = (): React.ReactElement => {
  const { t } = useTranslation()

  // workflow-back-bar-readonly AC-01: caseId は URL クエリパラメータ `?workflowId=`
  // から取得 (= ワークフローステップからの遷移時に resolveLinkedPage が付与)。
  // isDateLocked / lockedTargetDate は AC-WFW-006a (対象日固定): 日レンジ
  // (dayDate) のみケース対象日に固定し disabled にする (Decision 5)。
  const {
    isDateLocked,
    lockedTargetDate,
    isTargetDateUnavailable,
    refetchWorkflowCase,
    isWorkflowCaseFetching,
  } = useCurrentWorkflowCase()

  // 業務エリア設定の取得 (§4-2: getPersonalSettings BFF API から取得)。
  // AC-SDG-011 の対象は RPC を限定しない「API 通信エラー」なので、この取得の失敗も
  // ErrorLayout (全画面エラー) ではなく画面内のエラー導線で扱う (= throwOnError を
  // 本画面の呼び出しからだけ false に上書きする。他ページの挙動は変えない)。
  const settingsQuery = useGetPersonalSettingsQuery({ throwOnError: false })
  const { data: settingsData, isLoading: settingsLoading } = settingsQuery
  const businessAreaCodes =
    settingsData?.settings?.businessAreas != null &&
    settingsData.settings.businessAreas.length > 0
      ? settingsData.settings.businessAreas
      : AREA_CODE_OPTIONS

  const filterState = useFilterState(businessAreaCodes)
  const { filter: persistedFilter } = filterState

  // AC-WFW-006a Decision 5: caseId 付きで開いている間は日レンジ (dayDate) のみ
  // ケース対象日に固定する (週 / 月の起点は対象外)。永続 state (persistedFilter)
  // へは書き戻さない (Decision 3、page 側で bypass する)。取得完了までは永続値を
  // そのまま表示するが、日付操作 UI は disabled のため実質操作不可 (Decision 1)。
  const lockedDayDate =
    lockedTargetDate !== undefined ? toYmd(lockedTargetDate) : undefined
  const filter: GapFilterState =
    isDateLocked && lockedDayDate !== undefined
      ? { ...persistedFilter, dayDate: lockedDayDate }
      : persistedFilter
  // FilterBar へは filterState 全体 (setter 群込み) を渡す契約のため、対象日を
  // 差し替えた filter で束ね直す。setter は disabled UI からは呼ばれない。
  const effectiveFilterState: UseFilterStateReturn = { ...filterState, filter }

  // AC-WFW-006a Decision 1: 日レンジ (rangeType='day') はケース対象日が確定する
  // まで対象日依存のデータ取得を開始しない (Decision 5 で dayDate のみ固定する
  // ことに対応し、fetch も同じ境界で gate する)。週 / 月レンジは対象外
  // (Decision 5: 週 / 月の起点は固定しないため、取得も既存どおり)。
  const isDayRangeLocked = isDateLocked && filter.rangeType === 'day'
  const gapQueryReady = !isDayRangeLocked || lockedTargetDate !== undefined

  // filter.selectedAreaCodes は空 = 全業務エリアのポジティブ選択モデル (2026-08-12
  // dead-click 修正で checkbox → multi-select へ移行した際に導入)。 クエリ・表示 ―
  // どちらも実際に対象となるエリア一覧 (queryAreaCodes) を使う。
  const queryAreaCodes = resolveQueryAreaCodes(
    filter.selectedAreaCodes,
    businessAreaCodes,
  )

  const {
    data,
    isLoading: dataLoading,
    isFetching: dataFetching,
    isPlaceholderData: isShowingPreviousData,
    refetch: refetchGap,
    hasFailedWithoutData: gapHasFailedWithoutData,
    hasFailedAfterSuccess: gapHasFailedAfterSuccess,
  } = useGetSupplyDemandGap(filter, queryAreaCodes, { enabled: gapQueryReady })
  const settingsFetchError = resolveFetchErrorState(settingsQuery)

  // AC-SDG-011: 需給ギャップ・業務エリア設定のどちらの取得が失敗しても同じ画面内の
  // エラー導線に載せる。 失敗は 2 状態に分ける:
  // - まだ一度も成功していない失敗 = 表示できるデータが無いのでチャート描画エリアを
  //   エラーメッセージ + 再読み込みで置き換える
  // - 一度成功した後の再取得失敗 = チャートは描画を継続したまま上部のバナーで
  //   「表示中の値が最新でない」ことを知らせる (入札量判断の入力になる画面なので、
  //   古い数値を黙って表示し続けない)
  // 両立しうるが同時に 2 つ出すと何が起きたのか読めないため、 置き換え側を優先する
  // (置き換え側の UI も再読み込み導線を持つ)。
  // 日レンジ固定中にケース対象日を得られない時は需給を取得しないので、まだ一度も
  // 成功していない失敗と同じく置き換え側で出す (取得を止めたままの空を「データなし」
  // に見せない)。
  const isWorkflowTargetDateUnavailable =
    isDayRangeLocked && isTargetDateUnavailable
  const showFetchError =
    gapHasFailedWithoutData ||
    settingsFetchError.hasFailedWithoutData ||
    isWorkflowTargetDateUnavailable
  const showStaleDataAlert =
    !showFetchError &&
    (gapHasFailedAfterSuccess || settingsFetchError.hasFailedAfterSuccess)

  const isReloading =
    dataFetching ||
    settingsQuery.isFetching ||
    (isWorkflowTargetDateUnavailable && isWorkflowCaseFetching)

  const handleReloadClick = (): void => {
    // React Query の手動 refetch は `enabled: false` を迂回して queryFn を呼ぶため、
    // ここでも `gapQueryReady` で gate する。 gate しないと、 ケース対象日が未確定の
    // まま業務エリア設定の失敗で出た再読み込みを押した時に、 永続フィルタ側の日付で
    // 需給取得が飛び、 AC-WFW-006a Decision 1 の fail-closed を破る。
    if (gapQueryReady) {
      void refetchGap()
    }
    if (isWorkflowTargetDateUnavailable) {
      refetchWorkflowCase()
    }
    void settingsQuery.refetch()
  }

  const areaSeriesRows = useMemo(
    () => data?.areaSeries.map(convertAreaSeries) ?? [],
    [data],
  )

  // 内訳表示のカテゴリは表示中の全エリアから 1 回だけ集める (エリアごとに集めると
  // あるエリアに無い区分の分だけ色と並びがずれ、 AC-SDG-003 の「同一時間軸を上下に
  // スキャンしてエリア間を見比べる」 が成立しない)。
  const supplyCategories = useMemo(
    () => collectSupplyCategories(areaSeriesRows),
    [areaSeriesRows],
  )

  // 供給 Bar の系列記述子を **1 回だけ** 組み、 凡例と全エリアの GapChart
  // (Bar / tooltip) に同じ配列を渡す。 凡例側と Bar 側で別々に色・ラベルを
  // 解決すると、 片方だけ触った時に凡例が bar を偽って説明する。
  const supplySeries = useMemo(
    () => buildSupplySeries(filter.breakdownMode, supplyCategories, t),
    [filter.breakdownMode, supplyCategories, t],
  )

  const isLoading = settingsLoading || dataLoading

  // サマリ表 (design `_renderSummary`): 全エリア横断の単一表に平坦化
  const summaryRows = useMemo(
    () => buildSummaryRows(areaSeriesRows, convertAreaLabel),
    [areaSeriesRows],
  )

  // CSV ダウンロードモーダル
  const [isCsvModalOpen, setIsCsvModalOpen] = useState(false)
  const isSlot48Available = filter.rangeType === 'day'

  // 取得失敗でモーダルを隠す時は state 側も閉じる。 表示条件だけで隠すと、 再読み込みが
  // 成功した瞬間に閉じたはずのモーダルが勝手に開き直す。
  useEffect(() => {
    if (showFetchError) {
      setIsCsvModalOpen(false)
    }
  }, [showFetchError])

  const handleCsvDownload = useCallback(
    (fmt: 'base' | 'peak' | 'offpeak' | 'slot48') => {
      // TODO(#1362): fmt は現状ファイル名にのみ反映される。 出力内容は表示中
      // データ (= FilterBar の代表値 + 期間粒度) に従い、 fmt 別の行生成は未実装。
      // 粒度別 (base/peak/offpeak/slot48) の精密出力は #1362 で buildCsvRows に
      // format 引数を足して対応する。 モーダル側にもその旨を注記済み。
      const csv = buildCsvRows(areaSeriesRows, convertAreaLabel)
      // ファイル名の日付も画面の対象日と同じ JST 軸で出す。
      downloadCsv(
        `supply-demand-gap_${fmt}_${toYmd(startOfTodayJst())}.csv`,
        csv,
      )
      setIsCsvModalOpen(false)
    },
    [areaSeriesRows],
  )

  // 期間ラベル (CSV 用)
  const periodLabel = (() => {
    switch (filter.rangeType) {
      case 'day':
        return filter.dayDate
      case 'week':
        return filterState.weekRangeLabel
      case 'month':
        return filterState.monthRangeLabel
    }
  })()

  const areaLabels = queryAreaCodes.map(convertAreaLabel)

  const isSingleArea = queryAreaCodes.length === 1

  // エリアブロック領域にチャートの代わりに出す面 (取得失敗 / ロード中 / 空)。
  // null ならチャートを描画する。 3 状態とも同じ中央寄せの器に入るので、 器は
  // JSX 側に 1 つだけ置いて中身だけをここで決める。
  // 取得失敗を先に見る (失敗したら読み込み中の表示は出さない)。
  // 条件を変えた直後は前の条件の結果 (placeholder) を影の下に残す (UI 辞書
  // region-loading.md「条件を変えた時に領域を空にしない」)。
  const isChartAreaLoading =
    !showFetchError && (isLoading || isShowingPreviousData)
  // 取得失敗: 本文は title、 再読み込みの導線は Alert の右端 (UI 辞書
  // alert-placement.md 4: Tertiary sm)。
  const chartAreaPlaceholder = showFetchError ? (
    <CompactAlert status="error" title={t('page:supplyGap.error.fetchFailed')}>
      <TertiaryButton
        loading={isReloading}
        onClick={handleReloadClick}
        size="sm"
      >
        {t('page:supplyGap.error.reload')}
      </TertiaryButton>
    </CompactAlert>
  ) : isChartAreaLoading &&
    areaSeriesRows.length === 0 ? null : areaSeriesRows.length === 0 ? (
    <Box fontSize="md">{t('page:supplyGap.noData')}</Box>
  ) : null

  const breadcrumbItems = [
    { label: t('common:nav.supply') },
    { label: t('page:supplyGap.title'), bold: true },
  ]

  return (
    <WorkflowPageLayout
      breadcrumbItems={breadcrumbItems}
      notices={
        // 一度成功した後の再取得失敗 (AC-SDG-011 / 011a「画面上部にエラーバナー」)。
        // チャートは描画を継続したまま、 表示中の値が最新でないことを知らせる。
        // 置き場所は画面の最上部 = ページの `notices` (UI 辞書 alert-placement.md 1)。
        // 再読み込みの導線は本文の右端 (同 4。 Tertiary sm)。
        showStaleDataAlert ? (
          <CompactAlert
            description={t('page:supplyGap.error.staleDataDescription')}
            role="alert"
            status="error"
            title={t('page:supplyGap.error.staleDataTitle')}
          >
            <TertiaryButton
              loading={isReloading}
              onClick={handleReloadClick}
              size="sm"
            >
              {t('page:supplyGap.error.reload')}
            </TertiaryButton>
          </CompactAlert>
        ) : undefined
      }
    >
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
          overflow: 'hidden',
        }}
      >
        <FilterBar
          businessAreaCodes={businessAreaCodes}
          filterState={effectiveFilterState}
          isDateLocked={isDateLocked}
          isTargetDateConfirmed={lockedTargetDate !== undefined}
        />

        {/* メインコンテンツ。 [DS差異] design (`.sg-content-row`) は広画面でサマリ表左 (700px) |
            需給ギャップ右の 2 カラムだが、 ユーザー指定 (2026-07-13) で全幅の縦積みに変更:
            需給ギャップ (全幅・エリアは 2 列グリッド) → 横線 → 安全売却量・安全買い量 表 (全幅)。
            チャートカードの見出し (タイトル/ガイド/CSV/凡例) は design 同様データの有無に
            関わらず常時表示し、 データが無い時はエリアブロック領域だけを空状態にする。 */}
        <Box flex={1} overflowY="auto" minHeight={0}>
          <Box
            css={{
              display: 'flex',
              flexDirection: 'column',
              gap: '12px',
              width: '100%',
              padding: '8px 0',
            }}
          >
            {/* チャートカード (design `.sg-chart-card`): 全エリア共通の見出し + 凡例 + 各エリアブロック。
                器は UI 辞書 card.md の `.card` (角丸 lg + 影 shadow-sm)、 見出しは
                `.card-header` (余白 16px・下線) の 1 行: 左に見出し + ガイド、 右端に CSV。
                凡例は本文 (`.card-body`) の先頭 (screens/supply-gap.md 3 / 3e)。 */}
            <Box
              flexDirection="column"
              display="flex"
              overflow="hidden"
              width="100%"
              minWidth={0}
              background="Surface.primary"
              borderWidth="1px"
              borderColor="Border.lowEmphasis"
              borderRadius="lg"
              boxShadow="shadSm"
            >
              <Box
                flexDirection="column"
                flexShrink={0}
                gap={2}
                display="flex"
                padding={4}
                borderColor="Border.lowEmphasis"
                borderBottomWidth="1px"
              >
                {/* 上段: タイトル + ガイド (左) … CSV (右端固定)。 design はここに
                    「配置リセット」も置くが GridStack (並替/リサイズ) 自体を実装
                    しないため対象外 (defer)。 */}
                <Box alignItems="center" flexWrap="wrap" gap={2} display="flex">
                  {/* カード見出し。design `.card-header` = `--font-size-lg` (16px) / 600
                      「16px 統一」(css/components.css:557)。`label` は md (14px) で
                      フォームラベル用のため、見出しには当てない (2026-09-03 デザイナー指摘)。 */}
                  <Text textStyle="sectionTitle">
                    {t('page:supplyGap.chartTitle')}
                  </Text>
                  <GuidePopover
                    ariaLabel={t('page:supplyGap.guideAriaLabel')}
                    buttonLabel={t('page:supplyGap.guideButton')}
                    panelId="sg-guide-panel"
                    title={t('page:supplyGap.guideTitle')}
                  >
                    {/* 計算式 (8080 `.sg-guide-formula` = 等幅・md・high-emphasis・行間 1.7)。
                        箇条書きはガイド本文 (UI 辞書 guide-popover.md
                        `.ecl-guide-popover-body` = md・mid-emphasis)。 */}
                    <Box
                      as="p"
                      marginBottom={2}
                      padding={2}
                      color="Text.highEmphasis"
                      fontFamily="mono"
                      fontSize="md"
                      lineHeight="1.7"
                      background="Surface.secondary"
                      borderRadius="base"
                    >
                      {t('page:supplyGap.chartFormula')}
                    </Box>
                    <Box
                      as="ul"
                      paddingLeft="1.2em"
                      color="Text.midEmphasis"
                      fontSize="md"
                    >
                      <li>{t('page:supplyGap.guideSign')}</li>
                      <li>{t('page:supplyGap.guideSales')}</li>
                      <li>{t('page:supplyGap.guideDataSource')}</li>
                    </Box>
                  </GuidePopover>
                  {/* 単位 MW は各エリアのグラフの Y 軸の上端に出す (8080 と同じ。
                      GapChart の YAxis label)。 見出しには置かない。 */}
                  <Box marginLeft="auto">
                    {/* 取得できたデータが無い間は CSV を無効化する。 `buildCsvRows` は
                        空 rows でもヘッダ 1 行を返すため、 活性のままだと「ヘッダだけの
                        CSV が正常に落ちた」ように見えてしまう (サイレントフォールバック)。 */}
                    {/* 条件を変えて取得し直している間 (前の条件の値を影の下に残している間)
                        も出力しない。 出力の中身が画面の条件と食い違うため。 */}
                    <TertiaryButton
                      disabled={showFetchError || isShowingPreviousData}
                      onClick={() => setIsCsvModalOpen(true)}
                      size="sm"
                    >
                      <FaArrowDownToBracketRegular
                        boxSize={iconSize.labeledButton}
                      />
                      {t('page:supplyGap.csvDownload')}
                    </TertiaryButton>
                  </Box>
                </Box>
              </Box>

              {/* 本文 (design `.sg-chart-card > .card-body`: 余白 16px、 縦並び gap 12px)。
                  先頭に凡例、 その下に各エリアのグラフ。 */}
              <Box flexDirection="column" gap={3} display="flex" padding={4}>
                {/* 凡例 (design `#sg-legend`: フラット羅列・常時表示・中央寄せ)。
                    凡例の checkbox 一括トグルは対象外 (静的 swatch のまま)。 */}
                <Box
                  alignItems="center"
                  justifyContent="center"
                  flexWrap="wrap"
                  flexShrink={0}
                  gap="6px 14px"
                  display="flex"
                  aria-label={t('page:supplyGap.legendAriaLabel')}
                  role="list"
                >
                  <LegendSwatch
                    color={CATEGORY_COLOR_MAP.DEMAND_PLUS_SALES}
                    isLine
                    label={t('page:supplyGap.legend.demandPlusSales')}
                  />
                  <LegendSwatch
                    color={CATEGORY_COLOR_MAP.DEMAND_FORECAST}
                    dashed
                    isLine
                    label={t('page:supplyGap.legend.demandForecast')}
                  />
                  {supplySeries.map((series) => (
                    <LegendSwatch
                      key={series.dataKey}
                      color={series.color}
                      label={series.label}
                    />
                  ))}
                  <LegendSwatch
                    color={CATEGORY_COLOR_MAP.LONG}
                    label={t('page:supplyGap.legend.long')}
                  />
                  <LegendSwatch
                    color={CATEGORY_COLOR_MAP.SHORT}
                    label={t('page:supplyGap.legend.short')}
                  />
                </Box>

                {/* エリア別チャートブロック (design `#sg-charts-root`): GridStack は実装せず
                  静的に配置。 AC-SDG-003: 複数エリア選択時も**縦 1 列**に積み上げて
                  スクロールさせる。 同一時間軸を上下にスキャンしてエリア間の
                  ロング / ショートを見比べられるようにするため (例: 東京・関西を
                  縦に並べて東西スプレッドを検討する)。 旧実装は 2 列グリッド
                  (ユーザー指定 2026-07-13) だったが、 横並びは目線を横に振る形で
                  同一コマの比較がしづらいため issue #6314 の PdM 判断
                  (2026-08-25) で縦 1 列へ転換した。 */}
                <RegionLoading
                  gap={3}
                  gridTemplateColumns="1fr"
                  display="grid"
                  minHeight={
                    isChartAreaLoading && areaSeriesRows.length === 0
                      ? '200px'
                      : undefined
                  }
                  isLoading={isChartAreaLoading}
                >
                  {chartAreaPlaceholder !== null ? (
                    <Flex
                      alignItems="center"
                      justifyContent="center"
                      gridColumn="1 / -1"
                      height="200px"
                    >
                      {chartAreaPlaceholder}
                    </Flex>
                  ) : (
                    areaSeriesRows.map((series) => {
                      const label = convertAreaLabel(series.areaCode)
                      // 単一エリア時のチャート高は **px 固定**にする。 design 8080 の
                      // GridStack は `cellHeight: 70` × 単一エリア `h: 8` = **560px**
                      // (`supply/supply-gap.html` L879 / L923)。
                      //
                      // 旧 `60vh` は縦の大きいディスプレイほどグラフが伸び、 下の
                      // サマリ表 (安全売却量・安全買い量) が初期ビューポートから遠ざかって
                      // 気づけなかった (2026-09-02 デザイナー指摘「table の位置が画面
                      // 最下部なので気付きにくい」)。 1440x900 で 540px 相当だが、
                      // 縦 1440px の環境では 860px を超える。
                      //
                      // 8080 側にも「**旧 height:64vh 固定はグラフより高くなり下に無駄な
                      // 余白が出ていた (2026-07-06)**」 (同ファイル L47-48) と vh 固定を
                      // やめた経緯が残っており、 本変更はその判断に戻すもの。 AC に高さの
                      // 規定は無い。
                      //
                      // 複数エリア時の 400px は据え置く (8080 は `h: 4` = 280px だが、
                      // 指摘は単一エリア時の見え方についてであり、 複数時まで詰めるのは
                      // 別判断)。
                      return (
                        <Box
                          key={series.areaCode}
                          flexDirection="column"
                          flexShrink={0}
                          display="flex"
                          overflow="hidden"
                          height={isSingleArea ? '560px' : '400px'}
                          minHeight="320px"
                          background="Surface.primary"
                          borderWidth="1px"
                          borderColor="Border.lowEmphasis"
                          borderRadius="md"
                        >
                          <Box
                            alignItems="center"
                            flexShrink={0}
                            display="flex"
                            padding="2px 12px"
                            background="Surface.secondary"
                            borderColor="Border.lowEmphasis"
                            borderBottomWidth="1px"
                          >
                            <Text
                              textStyle="subSectionTitle"
                              color="Text.highEmphasis"
                            >
                              {label}
                            </Text>
                          </Box>
                          <Box
                            flexDirection="column"
                            flex={1}
                            display="flex"
                            minHeight={0}
                          >
                            <GapChart
                              areaLabel={label}
                              points={series.points}
                              supplySeries={supplySeries}
                            />
                          </Box>
                        </Box>
                      )
                    })
                  )}
                </RegionLoading>
              </Box>
            </Box>

            {/* サマリカード (design `.sg-summary-card`): 全エリア横断の単一表。
                [DS差異] ユーザー指定 (2026-07-13) で需給ギャップの下に全幅表示し、
                上端に横線 (borderTop) で区切る。 0 件時は非表示 (design 同様)。 */}
            {summaryRows.length > 0 && (
              <Box
                width="100%"
                paddingTop={3}
                borderColor="Border.lowEmphasis"
                borderTopWidth="1px"
              >
                {/* 条件を変えた直後は前の条件の表を影の下に残す (region-loading.md) */}
                <RegionLoading isLoading={isShowingPreviousData}>
                  <SubPeriodSummaryTable rows={summaryRows} />
                </RegionLoading>
              </Box>
            )}
          </Box>
        </Box>
      </div>

      {/* CSV ダウンロードモーダル */}
      <CsvDownloadModal
        areaLabels={areaLabels}
        // 開いた後にデータが失われた場合 (= フィルタ変更で query key が変わり、その取得が
        // 失敗した場合) も、ヘッダだけの CSV を出さないよう閉じる。 state 側は上の
        // useEffect が同時に落とす (= 再読み込み成功時に開き直さない)。 ここの条件式は
        // effect が流れる前の同一 render で既に隠すための即時ガード。
        isOpen={isCsvModalOpen && !showFetchError}
        isSlot48Available={isSlot48Available}
        onClose={() => setIsCsvModalOpen(false)}
        onDownload={handleCsvDownload}
        periodLabel={periodLabel}
      />
    </WorkflowPageLayout>
  )
}
